<script setup>
import { useI18n } from 'vue-i18n'
import Header from '@/components/Header'
import DesignFooter from '@/components/DesignFooter.vue'
import { childItem } from './contant.js'

const { t } = useI18n()
const data = childItem.aiToys
const asset = (path) => `${import.meta.env.BASE_URL}${path}`
const title = 'AI玩具解决方案'
const description = '移远通信以“连接+AI模型+算法+物联网平台”的一站式服务，为传统智能玩具的AI升级提供支撑。'
const painPoints = [
  { title: '连接不稳定', icon: 'connect' },
  { title: '交互响应慢', icon: 'timeout' },
  { title: '对话生硬无感情', icon: 'dialogue' },
]
const overviewImages = [
  { path: 'new-image/industry/toy-4.jpeg', alt: 'AI玩具的稳定连接、情绪理解与IoT平台支持' },
  { path: 'new-image/industry/toy-5.jpeg', alt: '移远AI玩具解决方案架构' },
  { path: 'new-image/industry/toy-6.jpeg', alt: '移远AI玩具解决方案交互与平台能力' },
]
const sceneImages = ['assistantApply1.png', 'assistantApply3.png', 'assistantApply4.png']
const caseImages = ['new-image/industry/toy-2.png', 'new-image/industry/toy-3.png', 'image/lastest-updates/update-2.jpg']
</script>

<template>
  <Header design-always hide-banner />
  <main class="smart-toys-page">
    <section class="hero" aria-labelledby="smart-toys-title">
      <img :src="asset('image/solution-redesign/smart-toys-banner.png')" class="hero-image" alt="" fetchpriority="high" />
      <div class="solution-container hero-content">
        <p class="breadcrumb">解决方案 / {{ title }}</p>
        <h1 id="smart-toys-title">{{ title }}</h1>
        <p class="hero-description">{{ description }}</p>
      </div>
    </section>

    <section class="solution-section muted" aria-labelledby="smart-toys-background">
      <div class="solution-container stack">
        <h2 id="smart-toys-background">业务背景</h2>
        <p class="background-copy">{{ data.businessBg }}</p>
      </div>
    </section>

    <section class="solution-section" aria-labelledby="smart-toys-pain">
      <div class="solution-container stack">
        <h2 id="smart-toys-pain">行业痛点</h2>
        <div class="pain-grid">
          <article v-for="point in painPoints" :key="point.icon" class="pain-card">
            <span class="pain-icon" aria-hidden="true"><img :src="asset(`svg/${point.icon}.svg`)" alt="" width="24" height="24" loading="lazy" /></span>
            <h3>{{ point.title }}</h3>
          </article>
        </div>
      </div>
    </section>

    <section class="solution-section muted" aria-labelledby="smart-toys-overview">
      <div class="solution-container stack">
        <h2 id="smart-toys-overview">方案概述</h2>
        <!-- 仓库内可信静态正文，按交付顺序展示三段能力与三张完整原图。 -->
        <div class="rich-copy" v-html="data.pectorophonyScheme" />
        <img v-for="image in overviewImages" :key="image.path" :src="asset(image.path)" :alt="image.alt" class="overview-image" loading="lazy" decoding="async" />
      </div>
    </section>

    <section class="solution-section" aria-labelledby="smart-toys-scenes">
      <div class="solution-container stack">
        <h2 id="smart-toys-scenes">应用场景</h2>
        <p class="section-description">按使用场景和设备条件选择能力。</p>
        <div class="scene-grid">
          <article v-for="(scene, index) in data.pectorophonyApply" :key="scene.name" class="scene-card">
            <img :src="asset(`image/${sceneImages[index]}`)" :alt="t(scene.name)" class="scene-image" loading="lazy" decoding="async" />
            <div class="scene-body"><h3>{{ t(scene.name) }}</h3><p>{{ scene.dec }}</p></div>
          </article>
        </div>
      </div>
    </section>

    <section class="solution-section muted" aria-labelledby="smart-toys-cases">
      <div class="solution-container stack">
        <h2 id="smart-toys-cases">相关案例</h2>
        <div class="case-grid">
          <a v-for="(item, index) in data.caseImg" :key="item.link" :href="item.link" class="case-card" v-track="{ position: 'solution > case', page: 'smart-toys', index: index }" target="_blank" rel="noopener noreferrer">
            <img :src="asset(caseImages[index])" alt="" class="case-image" loading="lazy" decoding="async" />
            <div class="case-body">{{ item.dec }}</div>
          </a>
        </div>
      </div>
    </section>
  </main>
  <DesignFooter />
</template>

<style scoped>
.smart-toys-page { min-width: 0; color: #14233b; background: #fff; }
.smart-toys-page, .smart-toys-page *, .smart-toys-page *::before, .smart-toys-page *::after { box-sizing: border-box; }
.solution-container { width: min(1440px, calc(100% - 48px)); margin-inline: auto; }
.smart-toys-page h1, .smart-toys-page h2, .smart-toys-page h3, .smart-toys-page p { margin: 0; }
.smart-toys-page h2 { color: #14233b; font-size: 24px; font-weight: 500; line-height: 1.4; text-align: center; }
.solution-section { padding-block: 64px; }
.muted { background: #f4f7fb; }
.stack { display: flex; flex-direction: column; gap: 24px; }
.hero { position: relative; display: flex; align-items: center; min-height: 352px; padding-block: 64px; color: #fff; background: #0d182b; }
.hero-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.hero-content { position: relative; }
.breadcrumb { margin-bottom: 8px !important; font-size: 14px; line-height: 24px; }
.hero h1 { margin-bottom: 16px; color: #fff; font-size: 30px; font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
.hero-description { font-size: 16px; line-height: 1.5; }
.background-copy, .section-description { color: #617089; font-size: 15px; line-height: 29px; text-align: center; }
.pain-grid, .case-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; align-items: stretch; }
.pain-card, .scene-card, .case-card { min-width: 0; border: 1px solid #dce2ea; border-radius: 16px; background: #fff; }
.pain-card { display: flex; align-items: center; gap: 16px; padding: 24px; }
.pain-icon { display: flex; flex: 0 0 40px; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 50%; background: #f4f7fb; }
.pain-icon img { display: block; width: 24px; height: 24px; }
.smart-toys-page h3 { color: #14233b; font-size: 18px; font-weight: 500; line-height: 1.5; }
.rich-copy { color: #617089; font-size: 15px; line-height: 29px; overflow-wrap: break-word; }
.rich-copy :deep(b) { color: #14233b !important; font-size: 18px; font-weight: 500; }
.overview-image { display: block; width: 100%; height: auto; border-radius: 16px; }
.scene-grid { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: 24px; }
.scene-card { display: flex; flex-direction: column; flex: 0 0 calc((100% - 48px) / 3); overflow: hidden; }
.scene-image { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
.scene-body { flex: 1; padding: 24px; }
.scene-body h3 { margin-bottom: 8px; }
.scene-body p { color: #617089; font-size: 14px; line-height: 24px; }
.case-card { display: flex; flex-direction: column; overflow: hidden; color: #14233b; text-decoration: none; transition: border-color 160ms ease, box-shadow 160ms ease; }
.case-image { display: block; width: 100%; aspect-ratio: 464 / 196; object-fit: contain; background: #fff; }
.case-body { flex: 1; padding: 24px; font-size: 16px; line-height: 24px; }
.case-card:hover { color: #14233b; border-color: #9baabd; box-shadow: 0 8px 24px rgb(20 35 59 / 8%); }
.case-card:focus-visible { outline: 3px solid #1478d4; outline-offset: 4px; }
@media (min-width: 768px) { .smart-toys-page h2 { font-size: 26px; } .hero h1 { font-size: 36px; } .hero-description { font-size: 18px; } }
@media (min-width: 992px) { .smart-toys-page h2 { font-size: 28px; } .hero h1 { font-size: 40px; } .hero-description { font-size: 18px; } }
@media (min-width: 1200px) { .smart-toys-page h2 { font-size: 30px; } .hero h1 { font-size: 44px; } .hero-description { font-size: 18px; } }
@media (min-width: 1400px) { .smart-toys-page h2 { font-size: 32px; } }
@media (max-width: 991px) { .pain-grid, .case-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .scene-card { flex-basis: calc((100% - 24px) / 2); } }
@media (max-width: 767px) {
  .solution-container { width: calc(100% - 32px); }
  .solution-section { padding-block: 40px; }
  .hero { min-height: 280px; padding-block: 40px; }
  .pain-grid, .case-grid { grid-template-columns: minmax(0, 1fr); }
  .scene-card { flex-basis: 100%; }
}
@media (prefers-reduced-motion: reduce) { .case-card { transition: none; } }
</style>